import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a buy now button component.
 *
 * @typedef {object} BuyNowRefs
 * @property {HTMLAnchorElement} BuyNowButton - The buy now button element.
 * @extends Component<BuyNowRefs>
 */
export class BuyNowComponent extends Component {
  requiredRefs = ['BuyNowButton'];

  #abortController = new AbortController();
  #isDisabled = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    
    // Listen for variant updates to update the button
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    
    // Also listen for variant selection to temporarily disable
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Disables the buy now button
   */
  disable() {
    const { BuyNowButton } = this.refs;
    if (BuyNowButton) {
      this.#isDisabled = true;
        BuyNowButton.setAttribute('disabled','')
    }
  }

  /**
   * Enables the buy now button
   */
  enable() {
    const { BuyNowButton } = this.refs;
    if (BuyNowButton) {
      this.#isDisabled = false;
      BuyNowButton.removeAttribute('disabled')
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { BuyNowButton } = this.refs;
    if (!BuyNowButton || !BuyNowButton.parentNode) return;

    // Get the new buy now button from the server response HTML
    const newBuyNowButton = event.detail.data.html.querySelector('buy-now-component [ref="BuyNowButton"]');
    
    if (newBuyNowButton) {
      // Clone the new button and replace the old one
      const clonedButton = newBuyNowButton.cloneNode(true);
      
      // Re-apply disabled styles if button was disabled
      if (this.#isDisabled) {
        clonedButton.setAttribute('disabled','')
      }
      
      BuyNowButton.parentNode.replaceChild(clonedButton, BuyNowButton);
      
      // Update the ref after replacement
      this.refs.BuyNowButton = clonedButton;
      
      // Re-enable the button if variant is available
      if (event.detail.resource != null && event.detail.resource.available !== false) {
        this.enable();
      } else {
        this.disable();
      }
    }
  };

  /**
   * Disable button while variant is being selected
   */
  #onVariantSelected = () => {
    this.disable();
  };
}

if (!customElements.get('buy-now-component')) {
  customElements.define('buy-now-component', BuyNowComponent);
}